// Game settings → Members (spec/dashboard.md section 12.4): the organization's owners and admins first, read-only (they
// hold every permission through the organization role), then the game members with their game role (`manage_members`),
// their time-boxed access (M7 slice 3) and Invite…, which opens the organization's invite dialog in place with this game
// preselected and its role select first (an invite is an organization resource that carries the game roles; it needs
// org_manage_members); after an invite the tab re-reads its lists and the accept link is shown once.
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { InviteButton } from '../org/Invites';
import { useOrgAccess } from '../org/orgAccess';
import { Badge } from '../../ui/Chip';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { queryCache, useQuery } from '../../workspace/cache';
import { GAME_ROLES } from './keys';
import { roleDescription, roleLabel } from '../../access/roles';
import { MembersTable, type MemberRow } from './MembersTable';
import { LoadError, SkeletonRows } from '../../ui/PageStates';

export function MembersTab() {
  const { org, game, missing, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const when = (v: string | null | undefined) => formatInstantWithZone(v, zone, { offsets });
  const membersKey = `game:${org}/${game.slug}:members`;
  const orgMembersKey = `org:${org}:members`;
  const membersQ = useQuery(membersKey, () => client.all((cursor) => client.data(ops.listGameMembers, { path: { org, game: game.slug }, query: { limit: 200, cursor } })));
  const orgQ = useQuery(orgMembersKey, () => client.all((cursor) => client.data(ops.listOrganizationMembers, { path: { org }, query: { limit: 200, cursor } })));
  const admins = (orgQ.data ?? []).filter((m) => m.role === 'owner' || m.role === 'admin');
  const blocked = missing('manage_members');
  const orgAccess = useOrgAccess(org);
  const reload = () => queryCache.invalidate(membersKey);
  const path = (m: MemberRow) => ({ org, game: game.slug, user: m.userId });

  return (
    <div data-testid="settings-members">
      <div className="pane settings-block" data-testid="settings-org-admins">
        <div className="pane-head">
          <h2>Organization owners and admins</h2>
          {admins.length > 0 && <span className="muted">{admins.length}</span>}
        </div>
        <p className="settings-note" role="note">
          They hold every permission through the organization role; change it on the organization's Members page.
        </p>
        <div className="pane-body flush">
        {orgQ.loading && !orgQ.data ? (
          <SkeletonRows rows={2} />
        ) : orgQ.error ? (
          <p className="settings-note">The organization members could not be read.</p>
        ) : admins.length === 0 ? (
          <p className="settings-note">None.</p>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Name</th>
                <th scope="col">Organization role</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((m) => (
                <tr key={m.userId} data-testid={`org-admin-${m.userId}`}>
                  <td>{m.email}</td>
                  <td>{m.displayName}</td>
                  <td>
                    <Badge tone={m.role === 'owner' ? 'accent' : 'info'} title={roleDescription('org', m.role)}>
                      {roleLabel('org', m.role)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>
      {membersQ.error ? (
        <LoadError>The game members could not be read.</LoadError>
      ) : null}
      <MembersTable
        caption="Game members"
        emptyText="No game members yet. Add someone by the email of their account."
        rows={membersQ.data ?? []}
        loading={membersQ.loading}
        roles={GAME_ROLES}
        roleKind="game"
        defaultRole="viewer"
        missing={blocked}
        when={when}
        ctx={{ offsets }}
        toolbar={
          <InviteButton
            org={org}
            missing={orgAccess.missing('org_manage_members')}
            game={{ slug: game.slug, name: game.name, role: 'viewer' }}
            onInvited={() => {
              reload();
              queryCache.invalidate(orgMembersKey);
            }}
          />
        }
        onExpiryChange={async (m, expiresAt) => {
          try {
            await client.call(ops.updateGameMember, { path: path(m), body: { expiresAt }, ifMatch: Number(m.version) });
          } finally {
            reload();
          }
        }}
        onRoleChange={async (m, role) => {
          try {
            await client.call(ops.updateGameMember, { path: path(m), body: { role }, ifMatch: Number(m.version) });
          } finally {
            reload();
          }
        }}
        onRemove={async (m) => {
          await client.call(ops.removeGameMember, { path: path(m) });
          reload();
        }}
        onAdd={async (email, role, expiresAt) => {
          await client.call(ops.addGameMember, { path: { org, game: game.slug }, body: expiresAt ? { email, role, expiresAt } : { email, role } });
          reload();
        }}
      />
    </div>
  );
}
